import { CheckCircle2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { LoadingButton } from '@/components/LoadingButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/components/theme-provider';
import { getLanguageLabel } from '@/constants/languages';

interface CompleteStepProps {
  isSubmitting: boolean;
  onComplete: () => void;
}

/**
 * Final step showing a summary of selections and completing onboarding
 */
export function CompleteStep({ isSubmitting, onComplete }: CompleteStepProps) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();


  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return t.settings.appearance.themeOptions.light;
      case 'dark':
        return t.settings.appearance.themeOptions.dark;
      case 'system':
        return t.settings.appearance.themeOptions.system;
      default:
        return theme;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete();
  };

  return (
    <Card className="h-[480px] flex flex-col">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4">
          <CheckCircle2 className="h-16 w-16 text-primary" />
        </div>
        <CardTitle>{t.userOnboardingWizard.completeStep.title}</CardTitle>
        <CardDescription>{t.userOnboardingWizard.completeStep.description}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        <CardContent className="space-y-4 flex-1">
          <div className="rounded-lg border p-4">
            <h4 className="text-sm font-medium mb-3">{t.userOnboardingWizard.completeStep.summary}</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t.userOnboardingWizard.completeStep.language}
                </span>
                <span className="font-medium">{getLanguageLabel(language, t)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t.userOnboardingWizard.completeStep.theme}
                </span>
                <span className="font-medium">{getThemeLabel()}</span>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center pt-4">
          <LoadingButton type="submit" isLoading={isSubmitting} className="min-w-[150px]">
            {t.userOnboardingWizard.completeStep.complete}
          </LoadingButton>
        </CardFooter>
      </form>
    </Card>
  );
}
